/**
 * MyNetwork — /network. network-style hub with tabs:
 *   invitations · connections · followers · following · suggestions.
 */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import ToolSelector from '../components/ToolSelector';
import PeopleYouMayKnow from '../components/network/PeopleYouMayKnow';
import NetworkOpportunities from '../components/network/NetworkOpportunities';
import SuggestedForYou from '../components/network/SuggestedForYou';
import CommunityAd from '../components/community/CommunityAd';
import { Button } from '../components/ui/button';
import {
  UserPlus, Users, Inbox, Sparkles, Check, X, Loader2, ArrowRight, Clock,
} from 'lucide-react';
import { formatDistanceToNow, parseISO } from 'date-fns';

const TABS = [
  { key: 'invitations', label: 'Invitations', Icon: Inbox },
  { key: 'connections', label: 'Connections', Icon: Users },
  { key: 'followers',   label: 'Followers',   Icon: UserPlus },
  { key: 'following',   label: 'Following',   Icon: ArrowRight },
  { key: 'suggestions', label: 'Suggestions', Icon: Sparkles },
];

const relTime = (iso) => {
  if (!iso) return '';
  try { return formatDistanceToNow(parseISO(iso), { addSuffix: true }); }
  catch { return ''; }
};

const MyNetwork = () => {
  const { API, user } = useContext(AuthContext);
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(params.get('tab') || 'invitations');
  const [incoming, setIncoming]     = useState([]);
  const [outgoing, setOutgoing]     = useState([]);
  const [connections, setConns]     = useState([]);
  const [followers, setFollowers]   = useState([]);
  const [following, setFollowing]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [busy, setBusy]             = useState({});

  const changeTab = (t) => { setTab(t); setParams({ tab: t }); };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [p, c, f1, f2] = await Promise.all([
        axios.get(`${API}/network/connect/pending`),
        axios.get(`${API}/network/connections`),
        user?.id ? axios.get(`${API}/network/${user.id}/followers`) : Promise.resolve({ data: { followers: [] } }),
        user?.id ? axios.get(`${API}/network/${user.id}/following`) : Promise.resolve({ data: { following: [] } }),
      ]);
      setIncoming(p.data?.incoming || []);
      setOutgoing(p.data?.outgoing || []);
      setConns(c.data?.connections || []);
      setFollowers(f1.data?.followers || []);
      setFollowing(f2.data?.following || []);
    } catch (e) { toast.error('Could not load network'); }
    finally { setLoading(false); }
  }, [API, user?.id]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const respond = async (requestId, action) => {
    setBusy((b) => ({ ...b, [requestId]: true }));
    try {
      await axios.put(`${API}/network/connect/${requestId}`, { action });
      toast.success(action === 'accept' ? 'Connection accepted' : 'Request declined');
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy((b) => ({ ...b, [requestId]: false })); }
  };

  const withdraw = async (memberId) => {
    setBusy((b) => ({ ...b, [`out-${memberId}`]: true }));
    try {
      await axios.delete(`${API}/network/connect/${memberId}`);
      toast.success('Request withdrawn');
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy((b) => ({ ...b, [`out-${memberId}`]: false })); }
  };

  const removeConn = async (memberId) => {
    if (!window.confirm('Remove this connection?')) return;
    setBusy((b) => ({ ...b, [`conn-${memberId}`]: true }));
    try {
      await axios.delete(`${API}/network/connect/${memberId}`);
      toast.success('Connection removed');
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy((b) => ({ ...b, [`conn-${memberId}`]: false })); }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="my-network-page">
      <ToolSelector />
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="mb-4">
          <div className="eyebrow text-primary">My Network</div>
          <h1 className="font-heading text-3xl font-extrabold text-foreground">Manage your connections</h1>
          <p className="text-sm text-muted-foreground mt-1">Accept invitations, discover new operators, and grow your circle.</p>
        </div>

        {/* Opportunities row */}
        <NetworkOpportunities />

        <div className="mb-4 flex flex-wrap gap-2 overflow-x-auto no-scrollbar" data-testid="my-network-tabs">
          {TABS.map((t) => {
            const active = tab === t.key;
            const count =
              t.key === 'invitations' ? incoming.length :
              t.key === 'connections' ? connections.length :
              t.key === 'followers'   ? followers.length :
              t.key === 'following'   ? following.length : null;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => changeTab(t.key)}
                data-testid={`network-tab-${t.key}`}
                className={`inline-flex items-center gap-1.5 rounded-full h-9 px-3.5 text-xs font-bold transition-colors flex-shrink-0 ${
                  active ? 'bg-primary text-primary-foreground' : 'bg-card border border-border text-foreground/75 hover:border-primary/60'
                }`}
              >
                <t.Icon className="w-3.5 h-3.5" /> {t.label}
                {count != null && count > 0 && (
                  <span className={`ml-1 h-5 min-w-[20px] px-1 rounded-full inline-flex items-center justify-center text-[10px] font-bold ${
                    active ? 'bg-primary-foreground/25 text-primary-foreground' : 'bg-muted text-foreground'
                  }`}>{count}</span>
                )}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-6">
          <div className="space-y-3">
            {loading && (
              <div className="pro-card p-8 text-center text-muted-foreground inline-flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
              </div>
            )}

            {!loading && tab === 'invitations' && (
              <>
                <div className="pro-card p-4" data-testid="incoming-invitations">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">
                    Incoming ({incoming.length})
                  </div>
                  {incoming.length === 0 ? (
                    <div className="text-sm text-muted-foreground italic">No invitations right now.</div>
                  ) : (
                    <ul className="space-y-2">
                      {incoming.map((r) => (
                        <li key={r.id} className="flex items-center gap-3 py-2 border-b border-border last:border-b-0" data-testid={`incoming-${r.id}`}>
                          <Link to={`/u/${r.member?.handle || r.member?.id}`} className="h-10 w-10 rounded-full bg-primary/15 text-primary flex items-center justify-center text-xs font-bold flex-shrink-0 overflow-hidden">
                            {r.member?.avatar_url
                              ? <img src={r.member.avatar_url} alt="" className="h-full w-full object-cover" />
                              : (r.member?.name || '?').slice(0, 2).toUpperCase()}
                          </Link>
                          <div className="min-w-0 flex-1">
                            <Link to={`/u/${r.member?.handle || r.member?.id}`} className="text-sm font-semibold truncate block hover:text-primary">
                              {r.member?.name}
                            </Link>
                            <div className="text-[11px] text-muted-foreground truncate">
                              {r.member?.headline || 'wants to connect'} · {relTime(r.created_at)}
                            </div>
                            {r.message && <div className="text-xs text-foreground mt-0.5 line-clamp-2">&ldquo;{r.message}&rdquo;</div>}
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <Button
                              size="sm"
                              onClick={() => respond(r.id, 'accept')}
                              disabled={busy[r.id]}
                              className="rounded-full h-8 text-xs font-bold"
                              data-testid={`accept-${r.id}`}
                            >
                              {busy[r.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Check className="w-3 h-3 mr-1" /> Accept</>}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => respond(r.id, 'decline')}
                              disabled={busy[r.id]}
                              className="rounded-full h-8 text-xs"
                              data-testid={`decline-${r.id}`}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {outgoing.length > 0 && (
                  <div className="pro-card p-4" data-testid="outgoing-invitations">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">
                      Sent ({outgoing.length})
                    </div>
                    <ul className="space-y-2">
                      {outgoing.map((r) => (
                        <li key={r.id} className="flex items-center gap-3 py-2 border-b border-border last:border-b-0" data-testid={`outgoing-${r.id}`}>
                          <Link to={`/u/${r.member?.handle || r.member?.id}`} className="h-9 w-9 rounded-full bg-primary/15 text-primary flex items-center justify-center text-xs font-bold flex-shrink-0 overflow-hidden">
                            {r.member?.avatar_url
                              ? <img src={r.member.avatar_url} alt="" className="h-full w-full object-cover" />
                              : (r.member?.name || '?').slice(0, 2).toUpperCase()}
                          </Link>
                          <div className="min-w-0 flex-1">
                            <Link to={`/u/${r.member?.handle || r.member?.id}`} className="text-sm font-semibold truncate block hover:text-primary">
                              {r.member?.name}
                            </Link>
                            <div className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                              <Clock className="w-3 h-3" /> Pending · sent {relTime(r.created_at)}
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => withdraw(r.member?.id)}
                            disabled={busy[`out-${r.member?.id}`]}
                            className="rounded-full h-8 text-xs"
                            data-testid={`withdraw-${r.member?.id}`}
                          >
                            Withdraw
                          </Button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}

            {!loading && tab === 'connections' && (
              <div className="pro-card p-4" data-testid="connections-list">
                {connections.length === 0 ? (
                  <div className="text-sm text-muted-foreground italic">No connections yet. Send an invitation from a member profile.</div>
                ) : (
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {connections.map((c) => (
                      <li key={c.id} className="flex items-center gap-3 rounded-lg border border-border p-3" data-testid={`connection-${c.id}`}>
                        <Link to={`/u/${c.handle || c.id}`} className="h-10 w-10 rounded-full bg-primary/15 text-primary flex items-center justify-center text-xs font-bold flex-shrink-0 overflow-hidden">
                          {c.avatar_url
                            ? <img src={c.avatar_url} alt="" className="h-full w-full object-cover" />
                            : (c.name || '?').slice(0, 2).toUpperCase()}
                        </Link>
                        <div className="min-w-0 flex-1">
                          <Link to={`/u/${c.handle || c.id}`} className="text-sm font-semibold truncate block hover:text-primary">{c.name}</Link>
                          {c.headline && <div className="text-[11px] text-muted-foreground truncate">{c.headline}</div>}
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => removeConn(c.id)}
                          disabled={busy[`conn-${c.id}`]}
                          className="rounded-full h-7 text-[11px]"
                        >
                          Remove
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {!loading && (tab === 'followers' || tab === 'following') && (
              <div className="pro-card p-4" data-testid={`${tab}-list`}>
                {((tab === 'followers' ? followers : following)).length === 0 ? (
                  <div className="text-sm text-muted-foreground italic">
                    {tab === 'followers' ? 'No followers yet.' : "You're not following anyone yet."}
                  </div>
                ) : (
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {(tab === 'followers' ? followers : following).map((c) => (
                      <li key={c.id} className="flex items-center gap-3 rounded-lg border border-border p-3">
                        <Link to={`/u/${c.handle || c.id}`} className="h-10 w-10 rounded-full bg-primary/15 text-primary flex items-center justify-center text-xs font-bold flex-shrink-0 overflow-hidden">
                          {c.avatar_url
                            ? <img src={c.avatar_url} alt="" className="h-full w-full object-cover" />
                            : (c.name || '?').slice(0, 2).toUpperCase()}
                        </Link>
                        <div className="min-w-0 flex-1">
                          <Link to={`/u/${c.handle || c.id}`} className="text-sm font-semibold truncate block hover:text-primary">{c.name}</Link>
                          {c.headline && <div className="text-[11px] text-muted-foreground truncate">{c.headline}</div>}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {!loading && tab === 'suggestions' && (
              <>
                <PeopleYouMayKnow />
                <div className="pro-card p-4 text-sm text-muted-foreground italic" data-testid="suggestions-empty-hint">
                  Suggestions get better as you follow operators and accept connections. Try connecting with a member from the <button type="button" onClick={() => changeTab('connections')} className="text-primary font-bold hover:underline">Connections</button> tab, or browse the <Link to="/directory" className="text-primary font-bold hover:underline">Directory</Link> to find peers with your gear.
                </div>
              </>
            )}
          </div>

          {/* Right rail */}
          <aside className="space-y-4">
            <div className="pro-card p-4">
              <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">Overview</div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg bg-muted p-2">
                  <div className="text-lg font-extrabold text-foreground">{connections.length}</div>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-widest font-bold">Connections</div>
                </div>
                <div className="rounded-lg bg-muted p-2">
                  <div className="text-lg font-extrabold text-foreground">{followers.length}</div>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-widest font-bold">Followers</div>
                </div>
                <div className="rounded-lg bg-muted p-2">
                  <div className="text-lg font-extrabold text-foreground">{following.length}</div>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-widest font-bold">Following</div>
                </div>
              </div>
            </div>
            {/* Sponsored — reuses the community ad rotator with a network placement */}
            <CommunityAd placement="network" />
            <SuggestedForYou compact />
            <PeopleYouMayKnow />
            {/* Featured Study Room CTA — always visible */}
            <div className="pro-card p-4 bg-gradient-to-br from-primary/5 to-transparent border border-primary/20" data-testid="network-study-cta">
              <div className="text-[10px] uppercase tracking-widest font-bold text-primary mb-1">
                Peer learning
              </div>
              <div className="font-heading font-extrabold text-sm mb-1">
                Cohort study sessions
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed mb-2.5">
                We auto-match hobbyists into weekly Study Buddy pairs. Toggle your preference in profile settings to get matched.
              </p>
              <Link to="/study-rooms" className="inline-flex items-center gap-1 text-xs text-primary font-bold hover:underline">
                Open Study Rooms →
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default MyNetwork;
